/**
 * Preview-branch SSO shim, browser side (the I/O half; see
 * src/lib/preview-auth-shim.ts for the decisions and the tests).
 *
 * Two jobs, both only ever reached on a pr-<N> preview host:
 *
 *   1. `previewShimSignInUrl` starts a normal Okta OIDC sign-in **against dev**
 *      (whose callback Okta accepts) and returns the URL to navigate to, with
 *      this preview as the redirect target.
 *   2. `PreviewShimCallback` finishes it: redeems dev's PKCE code for a dev
 *      session, hands that token to this project's preview-auth-shim function,
 *      and redeems the one-time token it returns. The resulting session belongs
 *      to the preview's own project, so its RLS and refresh behave normally.
 *
 * Design + trust model: docs/plans/PREVIEW_BRANCH_AUTH_SHIM.md
 */
import { useEffect, useRef, useState } from "react";
import { createClient } from "@supabase/supabase-js";
import type { Provider } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import auroraLogo from "@/assets/aurora-brand-logo.svg";
import {
  shimExchangeHeaders,
  shimExchangeUrl,
  type PreviewAuthShimConfig,
  type PreviewShimCallback,
} from "@/lib/preview-auth-shim";

/** Kept out of the app's own storage keys: this session is only ever a proof. */
const SHIM_STORAGE_KEY = "sb-preview-shim-auth-token";

const EXCHANGE_TIMEOUT_MS = 15_000;

/**
 * A client for the dev project, used only to run the OIDC handshake and hold
 * the PKCE verifier across the round trip. `detectSessionInUrl` is off because
 * this component reads dev's callback out of the URL itself, and dev's session
 * must never end up in this project's storage.
 *
 * `flowType: "pkce"` is explicit because supabase-js defaults to the implicit
 * flow, which is what the app's own client uses. Without it the authorize
 * request carries no code challenge, dev answers with a session in the URL
 * fragment, and there is nothing to redeem.
 */
function createDevShimClient(config: PreviewAuthShimConfig) {
  return createClient(config.devUrl, config.devPublishableKey, {
    auth: {
      storageKey: SHIM_STORAGE_KEY,
      persistSession: true,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      flowType: "pkce",
    },
  });
}

/** The URL to send the browser to, or a thrown error if dev refused to start. */
export async function previewShimSignInUrl(
  config: PreviewAuthShimConfig,
  redirectTo: string,
): Promise<string> {
  const client = createDevShimClient(config);
  const { data, error } = await client.auth.signInWithOAuth({
    // supabase-js's Provider union doesn't list custom:* providers yet; the
    // value is passed through to /auth/v1/authorize unchanged (as in Auth.tsx).
    provider: config.provider as Provider,
    options: { redirectTo, skipBrowserRedirect: true },
  });
  if (error) throw error;
  if (!data?.url) throw new Error("Dev did not return a sign-in URL");
  return data.url;
}

/**
 * Redeem dev's PKCE code, convert the resulting dev identity into a session on
 * this preview's own project, then drop the dev session.
 *
 * The order matters. `signOut` reaches dev's `/auth/v1/logout` even with
 * `scope: "local"`, and GoTrue revokes the session there, so the access token
 * stops working. Signing out before the exchange invalidates the very token the
 * shim function verifies, which surfaces as "Dev session rejected" on a
 * perfectly good sign-in.
 */
async function finishShimSignIn(
  config: PreviewAuthShimConfig,
  callback: PreviewShimCallback,
): Promise<void> {
  if (callback.kind === "error") throw new Error(callback.message);

  const client = createDevShimClient(config);
  const { data, error } = await client.auth.exchangeCodeForSession(callback.code);
  if (error || !data?.session) {
    throw new Error(error?.message ?? "The dev sign-in could not be completed");
  }

  try {
    await mintPreviewSession(data.session.access_token);
  } finally {
    await client.auth.signOut({ scope: "local" }).catch(() => undefined);
  }
}

/** Convert a dev identity into a session on this preview's own project. */
async function mintPreviewSession(devAccessToken: string): Promise<void> {
  const branchUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const branchKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
  if (!branchUrl || !branchKey) {
    throw new Error("This preview is not configured with its own Supabase project");
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), EXCHANGE_TIMEOUT_MS);
  let payload: { token_hash?: string; verification_type?: "magiclink" | "signup"; error?: string };
  try {
    const response = await fetch(shimExchangeUrl(branchUrl), {
      method: "POST",
      headers: shimExchangeHeaders(branchKey, devAccessToken),
      body: "{}",
      signal: controller.signal,
    });
    payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload?.error ?? `Preview sign-in was refused (HTTP ${response.status})`);
    }
  } catch (err) {
    if ((err as Error)?.name === "AbortError") {
      throw new Error("The preview sign-in service did not respond");
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }

  if (!payload.token_hash || !payload.verification_type) {
    throw new Error("The preview sign-in service returned no session");
  }
  const { error: verifyError } = await supabase.auth.verifyOtp({
    token_hash: payload.token_hash,
    type: payload.verification_type,
  });
  if (verifyError) throw verifyError;
}

/**
 * Landing state for a preview sign-in. `useAuth`'s onAuthStateChange takes over
 * once the session exists, and the route guard moves the user into the app.
 */
export function PreviewShimCallback({
  config,
  callback,
}: {
  config: PreviewAuthShimConfig;
  callback: PreviewShimCallback;
}) {
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    // Drop dev's callback out of the URL either way, so a reload cannot replay
    // a spent code and a shared URL cannot carry dev's session fragment.
    window.history.replaceState({}, "", "/auth");

    if (callback.kind === "error") {
      setError(callback.message);
      return;
    }
    finishShimSignIn(config, callback).catch((err: Error) => {
      console.error("[Auth] preview shim exchange failed", err);
      setError(err?.message ?? "Preview sign-in failed");
    });
  }, [config, callback]);

  const retry = () => {
    window.location.assign(`${window.location.origin}/auth`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md animate-fade-in">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img src={auroraLogo} alt="Aurora logo" className="h-12 w-12 object-contain" />
          </div>
          <CardTitle className="text-2xl">{error ? "Sign-in failed" : "Signing you in"}</CardTitle>
          <CardDescription>
            {error
              ? "ATMS could not complete the sign-in for this preview."
              : "Finishing the Aurora sign-in for this preview build…"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {error ? (
            <>
              <p className="text-sm text-muted-foreground break-words">{error}</p>
              <Button className="w-full" onClick={retry}>
                Try again
              </Button>
            </>
          ) : (
            <div className="animate-pulse text-sm text-muted-foreground text-center">
              This takes a moment.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
